import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoMembershipService from "@saccoMembershipService";

const QUERY_KEY = 'sacco_memberships';

/**
 * Custom React hook to fetch the list of Sacco memberships for the currently authenticated user.
 *
 * The hook uses the `useAuth` hook to retrieve the authenticated user's information and triggers
 * a query using `useQuery` from the React Query library. It fetches all Sacco memberships for the
 * user by calling the `fetchAll` method from the `saccoMembershipService`.
 *
 * The query is enabled only when a user is authenticated (`user` is not null or undefined).
 *
 * @return {object} Returns an object containing the state and methods for managing the query, such as
 *                  `data`, `isLoading`, `isError`, and other properties provided by React Query.
 */
export function useFetchSaccoMemberships() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      return await saccoMembershipService.fetchAll(user.id);
    },
    enabled: !!user,
  });
}

export function useCreateSaccoMembership() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (request: SaccoMembershipCreateRequest) => {
      return await saccoMembershipService.create(user.id, request);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateSaccoMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updateRequest: SaccoMembershipUpdateRequest) => {
      return await saccoMembershipService.update(updateRequest);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useDeleteSaccoMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await saccoMembershipService.delete(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}
